/**
 * PrivacyModeContext — the master "quiet-the-whole-app" switch.
 *
 * ON  — one tap. Composes: hides all tagged amounts, replaces the header
 *        Code Name with "MEMBER", hides the Settings tier badge, dims
 *        walkthrough video thumbnails. Sets `html[data-privacy-mode=true]`
 *        so any CSS built for the co-viewing mask kicks in automatically.
 * OFF — requires the member's password (via /api/auth/verify-password).
 *        Stops nosy neighbours from just tapping it back off.
 *
 * State is per-device (localStorage) — the founder was clear that a
 * member's laptop in a café has different risks than their phone at home.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { getToken } from '../utils/tokenManager';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'mif:privacy_mode';

const PrivacyModeContext = createContext({
  privacyMode: false,
  turnOn: () => {},
  turnOff: async () => ({ ok: false, error: 'not initialised' }),
});

const readInitial = () => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; }
  catch { return false; }
};

// Privacy Mode uses a distinctly STRONGER visual than Hide Amounts.
// - Hide Amounts (eye button) paints dashed bars over tagged spans — the
//   "quick peek shield" for casual glances.
// - Privacy Mode BLURS tagged spans instead of dashing them, plus paints a
//   thin emerald frame around the viewport so members can tell at a glance
//   that the strong lock is engaged. The blur says "there is content here
//   but you can't read it" — a materially different signal from dashes,
//   which say "we're just hiding a number."
const PRIVACY_MODE_STYLES = `
[data-privacy-mode="true"] [data-sensitive="monetary"],
[data-privacy-mode="true"] [data-sensitive="currency"],
[data-privacy-mode="true"] [data-sensitive="rate"],
[data-privacy-mode="true"] [data-sensitive="asset-name"],
[data-privacy-mode="true"] [data-sensitive="identifier"] {
  /* Override Hide Amounts' dashed mask if it happens to also be on — the
     stronger lock always wins visually. */
  color: revert !important;
  background: none !important;
  filter: blur(6px) !important;
  user-select: none;
  pointer-events: none;
  transition: filter 180ms ease-out;
}
[data-privacy-mode="true"] [data-sensitive="chart"],
[data-privacy-mode="true"] canvas[data-sensitive] {
  filter: blur(14px) !important;
  pointer-events: none;
  transition: filter 180ms ease-out;
}
[data-privacy-mode="true"] [data-walkthrough-thumbnail] {
  filter: grayscale(1) opacity(0.5);
  pointer-events: none;
}
/* Thin emerald frame — a calm, always-on visual cue that the strong lock
   is engaged. Fixed to the viewport so it survives scrolling. */
[data-privacy-mode="true"]::before {
  content: '';
  position: fixed;
  inset: 0;
  border: 2px solid rgba(16, 185, 129, 0.55);
  border-radius: 4px;
  pointer-events: none;
  z-index: 9998;
}
`;

export function PrivacyModeProvider({ children }) {
  const [privacyMode, setState] = useState(readInitial);

  // Inject the layered stylesheet once.
  useEffect(() => {
    if (document.getElementById('privacy-mode-styles')) return;
    const el = document.createElement('style');
    el.id = 'privacy-mode-styles';
    el.textContent = PRIVACY_MODE_STYLES;
    document.head.appendChild(el);
  }, []);

  // Reflect state on the document root + persist.
  useEffect(() => {
    document.documentElement.setAttribute(
      'data-privacy-mode', privacyMode ? 'true' : 'false'
    );
    try { localStorage.setItem(STORAGE_KEY, privacyMode ? '1' : '0'); }
    catch { /* private mode */ }
  }, [privacyMode]);

  const turnOn = useCallback(() => setState(true), []);

  const turnOff = useCallback(async (password) => {
    try {
      const res = await fetch(`${API}/api/auth/verify-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.detail || 'Incorrect password.' };
      }
      setState(false);
      return { ok: true };
    } catch {
      return { ok: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const value = useMemo(
    () => ({ privacyMode, turnOn, turnOff }),
    [privacyMode, turnOn, turnOff]
  );

  return (
    <PrivacyModeContext.Provider value={value}>
      {children}
    </PrivacyModeContext.Provider>
  );
}

export function usePrivacyMode() {
  return useContext(PrivacyModeContext);
}
